import { reactive } from 'vue'

// Single app-wide dialog, rendered once by AppDialog.vue at the root.
//
// Two ways in:
//   1. dialog.alert/confirm/loading - the API to write new code against.
//   2. a SweetAlert2-shaped `fire()` - so the 76 existing call sites migrate by
//      changing one import line instead of being rewritten by hand. That shim
//      covers exactly the options this codebase actually passes; anything else
//      is ignored on purpose rather than half-implemented.
//
// Dropping sweetalert2 also drops a dependency that ships its own stylesheet
// and injects its own DOM, which is why theming it to match the app was never
// going to look right.

const ICONS = {
  success: 'mdi-check-circle-outline',
  error: 'mdi-alert-circle-outline',
  warning: 'mdi-alert-outline',
  info: 'mdi-information-outline',
  question: 'mdi-help-circle-outline',
}

export const dialogState = reactive({
  visible: false,
  phase: 'info', // success | error | warning | info | question | loading
  icon: null,
  title: '',
  message: '',
  html: '',
  loading: false,
  showConfirm: true,
  showCancel: false,
  confirmText: 'OK',
  cancelText: 'Cancel',
  confirmColor: null,
  cancelColor: null,
  reverseButtons: false,
  dismissOnBackdrop: true,
  dismissOnEscape: true,
  width: null,
})

let resolveCurrent = null
let autoCloseTimer = null

const clearTimer = () => {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
    autoCloseTimer = null
  }
}

// Every exit path funnels through here so a caller awaiting the promise is
// resolved exactly once, whether the user tapped a button, tapped the
// backdrop, pressed escape, or the timer ran out.
const settle = outcome => {
  clearTimer()
  dialogState.visible = false
  dialogState.loading = false
  if (resolveCurrent) {
    const resolve = resolveCurrent
    resolveCurrent = null
    resolve(outcome)
  }
}

const confirmed = () => settle({ isConfirmed: true, isDismissed: false, isDenied: false, value: true })
const dismissed = reason =>
  settle({ isConfirmed: false, isDismissed: true, isDenied: false, dismiss: reason || 'close' })

export const closeDialog = () => dismissed('close')
export const confirmDialog = () => confirmed()
export const backdropDismiss = () => {
  if (dialogState.dismissOnBackdrop) dismissed('backdrop')
}
export const escapeDismiss = () => {
  if (dialogState.dismissOnEscape) dismissed('esc')
}

const open = options => {
  clearTimer()

  // A dialog opening while another is awaited would strand the first promise.
  if (resolveCurrent) settle({ isConfirmed: false, isDismissed: true, isDenied: false, dismiss: 'superseded' })

  Object.assign(dialogState, {
    visible: true,
    phase: options.phase || 'info',
    icon: options.icon || ICONS[options.phase] || null,
    title: options.title || '',
    message: options.message || '',
    html: options.html || '',
    loading: Boolean(options.loading),
    showConfirm: options.showConfirm !== false && !options.loading,
    showCancel: Boolean(options.showCancel),
    confirmText: options.confirmText || 'OK',
    cancelText: options.cancelText || 'Cancel',
    confirmColor: options.confirmColor || null,
    cancelColor: options.cancelColor || null,
    reverseButtons: Boolean(options.reverseButtons),
    dismissOnBackdrop: options.dismissOnBackdrop !== false,
    dismissOnEscape: options.dismissOnEscape !== false,
    width: options.width || null,
  })

  const promise = new Promise(resolve => {
    resolveCurrent = resolve
  })

  if (options.timer) autoCloseTimer = setTimeout(() => dismissed('timer'), options.timer)

  return promise
}

export const dialog = {
  alert: options => open({ phase: 'info', ...options }),
  success: (title, message) => open({ phase: 'success', title, message }),
  error: (title, message) => open({ phase: 'error', title, message }),
  warning: (title, message) => open({ phase: 'warning', title, message }),
  confirm: options =>
    open({
      phase: 'question',
      showCancel: true,
      confirmText: 'Confirm',
      ...options,
    }).then(result => result.isConfirmed),
  // Blocking progress. No buttons, no backdrop dismiss: the caller closes it.
  loading: (title, message = '') =>
    open({
      phase: 'loading',
      title,
      message,
      loading: true,
      showConfirm: false,
      dismissOnBackdrop: false,
      dismissOnEscape: false,
    }),
  close: closeDialog,
}

// --- SweetAlert2 compatibility -------------------------------------------
// Only the options this codebase passes. Verified against all 76 call sites:
// title, text, html, icon, timer, width, reverseButtons, allowOutsideClick,
// allowEscapeKey, show/confirm/cancel button text and colour, didOpen, willOpen.

const fire = (options = {}) => {
  const opts = typeof options === 'string' ? { title: options } : options

  if (typeof opts.willOpen === 'function') opts.willOpen()

  const promise = open({
    phase: opts.icon || 'info',
    title: opts.title,
    message: opts.text,
    html: opts.html,
    showConfirm: opts.showConfirmButton !== false,
    showCancel: Boolean(opts.showCancelButton),
    confirmText: opts.confirmButtonText || 'OK',
    cancelText: opts.cancelButtonText || 'Cancel',
    confirmColor: opts.confirmButtonColor,
    cancelColor: opts.cancelButtonColor,
    reverseButtons: opts.reverseButtons,
    dismissOnBackdrop: opts.allowOutsideClick !== false,
    dismissOnEscape: opts.allowEscapeKey !== false,
    timer: opts.timer,
    width: opts.width,
  })

  // didOpen is where this codebase calls Swal.showLoading(). Run it after the
  // state flip so that call lands on an already-open dialog.
  if (typeof opts.didOpen === 'function') opts.didOpen()

  return promise
}

const Swal = {
  fire,
  close: closeDialog,
  // Turns the open dialog into a spinner and hides its buttons, matching
  // Swal.showLoading()'s behaviour at the call sites that use it.
  showLoading: () => {
    dialogState.loading = true
    dialogState.phase = 'loading'
    dialogState.showConfirm = false
    dialogState.showCancel = false
    dialogState.dismissOnBackdrop = false
  },
  hideLoading: () => {
    dialogState.loading = false
    dialogState.showConfirm = true
  },
  update: (options = {}) => {
    if (options.title !== undefined) dialogState.title = options.title
    if (options.text !== undefined) dialogState.message = options.text
    if (options.icon !== undefined) {
      dialogState.phase = options.icon
      dialogState.icon = ICONS[options.icon] || null
    }
  },
  isVisible: () => dialogState.visible,
}

export default Swal
